import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { workshopService } from '../../services/workshop.service';
import { CreateWorkshopRequest, WorkshopCategory, WorkshopDifficulty } from '../../types/workshop.types';
import { useNotification } from '../../context/NotificationContext';

const categoryOptions: { value: WorkshopCategory; label: string }[] = [
  { value: 'privilege_cloud', label: 'Privilege Cloud' },
  { value: 'identity_security', label: 'Identity Security' },
  { value: 'pam_self_hosted', label: 'PAM Self-Hosted' },
  { value: 'secrets_manager', label: 'Secrets Manager' },
  { value: 'endpoint_privilege_manager', label: 'Endpoint Privilege Manager' },
  { value: 'other', label: 'Other' },
];

const difficultyOptions: { value: WorkshopDifficulty; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

const CreateWorkshop: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'other' as WorkshopCategory,
    difficulty: 'beginner' as WorkshopDifficulty,
    maxStudents: 20 as number | string,
    tags: [] as string[],
    prerequisites: '',
    learningObjectives: '',
    allowHelpRequests: true,
    enforceStepOrder: true,
  });
  const [tagsInput, setTagsInput] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  type FormState = typeof form;
  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field: 'maxStudents', value: string) => {
    if (value === '') {
      updateField(field, '');
      return;
    }
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed)) {
      updateField(field, parsed);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || form.title.trim().length < 3) {
      showNotification('Title must be at least 3 characters', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload: CreateWorkshopRequest = {
        ...form,
        estimatedDuration: 0,
        maxStudents: Number(form.maxStudents) || 20,
        tags: tagsInput ? tagsInput.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      const workshop = await workshopService.createWorkshop(payload);
      showNotification('Tech Eval created!', 'success');
      navigate(`/instructor/workshop/${workshop.id}/edit`);
    } catch {
      showNotification('Failed to create tech eval', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/instructor')}
        sx={{ mb: 2 }}
      >
        Back to Tech Evals
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
        Create New Tech Eval
      </Typography>

      <Paper sx={{ maxWidth: 900, mx: 'auto', p: '40px' }}>
        {/* Section 1: Basic Information */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Basic Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set the title and description for your tech eval
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Title"
          required
          placeholder="e.g., Privilege Cloud Safe Management"
          value={form.title}
          onChange={(e) => updateField('title', e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
          error={touched.title && !form.title?.trim()}
          helperText={touched.title && !form.title?.trim() ? 'Title is required' : ''}
          sx={{ mb: 3 }}
        />
        <TextField
          fullWidth
          label="Description"
          multiline
          minRows={4}
          placeholder="Describe what students will learn..."
          value={form.description}
          onChange={(e) => updateField('description', e.target.value)}
          sx={{ mb: 4 }}
        />

        {/* Section 2: Workshop Configuration */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Tech Eval Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure category, difficulty, and capacity settings
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              select
              label="Category"
              value={form.category}
              onChange={(e) => updateField('category', e.target.value as WorkshopCategory)}
            >
              {categoryOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              select
              label="Difficulty"
              value={form.difficulty}
              onChange={(e) => updateField('difficulty', e.target.value as WorkshopDifficulty)}
            >
              {difficultyOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Max Students"
              type="number"
              value={form.maxStudents}
              onChange={(e) => handleNumberChange('maxStudents', e.target.value)}
              inputProps={{ min: 1, max: 100 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Duration will be calculated automatically from step times after you add steps.
            </Typography>
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Tags"
          placeholder="e.g., privilege-cloud, safes, security"
          helperText="Comma-separated tags"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          sx={{ mb: 4 }}
        />

        {/* Section 3: Learning Details */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Learning Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define prerequisites and learning objectives
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Prerequisites"
          multiline
          minRows={3}
          placeholder="List any prerequisites..."
          value={form.prerequisites}
          onChange={(e) => updateField('prerequisites', e.target.value)}
          sx={{ mb: 3 }}
        />
        <TextField
          fullWidth
          label="Learning Objectives"
          multiline
          minRows={3}
          placeholder="What will students achieve..."
          value={form.learningObjectives}
          onChange={(e) => updateField('learningObjectives', e.target.value)}
          sx={{ mb: 4 }}
        />

        {/* Section 4: Settings */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure tech eval behavior
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.allowHelpRequests}
                onChange={(e) => updateField('allowHelpRequests', e.target.checked)}
              />
            }
            label="Enable help requests during tech eval"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            Students can request assistance from the instructor at any step
          </Typography>
        </Box>
        <Box sx={{ mb: 4 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.enforceStepOrder}
                onChange={(e) => updateField('enforceStepOrder', e.target.checked)}
              />
            }
            label="Enforce step order (students must complete steps sequentially)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            Students cannot skip ahead to later steps without completing earlier ones
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/instructor')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !form.title}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save as Draft'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateWorkshop;
